import { Card, CardContent, CardHeader, CardTitle, cn } from "@exhousing/design-system";
import { toast } from "sonner";

import { useUpdateProperty } from "@/hooks/api/useProperties";

interface Props {
  propertyId: string;
  isPublished: boolean;
  publishToMarketplace: boolean;
  channels: Record<string, boolean>;
}

const EXTRA_CHANNELS = [
  { key: "pararius", label: "Pararius" },
  { key: "funda", label: "Funda" },
  { key: "agency_website", label: "Agency website" },
];

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 py-2.5">
      <span>
        <span className="text-sm font-medium">{label}</span>
        {description && (
          <span className="block text-xs text-muted-foreground">{description}</span>
        )}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
          checked ? "bg-accent" : "bg-sand-300",
        )}
      >
        <span
          className={cn(
            "inline-block size-5 transform rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-5" : "translate-x-0.5",
          )}
        />
      </button>
    </label>
  );
}

export function PublicationCard({
  propertyId,
  isPublished,
  publishToMarketplace,
  channels,
}: Props) {
  const update = useUpdateProperty(propertyId);

  const patch = (body: Record<string, unknown>) =>
    update.mutate(body, { onError: () => toast.error("Could not update publication") });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Publication</CardTitle>
      </CardHeader>
      <CardContent className="divide-y divide-border/60 py-0">
        <Toggle
          label="Published"
          description="Visible in the back-office as live"
          checked={isPublished}
          onChange={(v) => patch({ is_published: v })}
        />
        <Toggle
          label="Platform marketplace"
          description="List on the public Balatin marketplace"
          checked={publishToMarketplace}
          onChange={(v) => patch({ publish_to_marketplace: v })}
        />
        {EXTRA_CHANNELS.map((c) => (
          <Toggle
            key={c.key}
            label={c.label}
            checked={Boolean(channels?.[c.key])}
            onChange={(v) =>
              patch({ publication_channels: { ...channels, [c.key]: v } })
            }
          />
        ))}
      </CardContent>
    </Card>
  );
}
